import MDinput from '@/components/MDinput';
import { validatorRequire, validatorRequireNumber, validatorConditionalRequired } from '@/utils/validators';
import Select from '@/components/DSE/Select';
import { FinishTypeOption } from '@/utils/constants';
import InputNumber from '@/components/DSE/InputNumber';
import FabricTypesList from './components/FabricTypesList';
import FinishTypesList from './components/FinishTypesComponent';

export function getFormDefinition(vue) {
  return {
    type: 'tab',
    saveButtons: [true, false, false, false], // Page, tab 1, tab 2, ...
    showFormLabels: [true, false, false, false], // Tab 1, tab 2, ....
    labels: ['common.tabGeneral', 'productGroup.tabPossibleFinishes', 'productGroup.tabPossibleFabricProductTypes'],
    headerTemplate: (str, form) => `
    <h3>
    ${form.description || ''}
    </h3>
    `,
    groups: [
      {
        code: {
          type: MDinput,
          defaultValue: '',
          props: {
            maxlength: 16
          },
          rules: [
            {
              validator: validatorRequire
            }
          ]
        },
        description: {
          type: MDinput,
          defaultValue: '',
          props: {
            maxlength: 32
          },
          rules: [
            {
              validator: validatorRequire
            }
          ]
        },
        productGroupTypeOption: {
          type: Select,
          defaultValue: '',
          props: {
            caption: 'productGroup.productGroupTypeOption',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('productGroupType'),
            required: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        defaultParcelTypeID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'productGroup.parcelType',
            required: true,
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['parcelType/isLoading'],
            options: vue.$store.getters['parcelType/loadedItems']
          }
        },
        defaultTopFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Top
            )
          }
        },
        defaultTopFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: null,
          props: {
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: !!form.defaultTopFinishID
            };
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.defaultTopFinishID) }]
        },
        defaultSideFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Side
            )
          }
        },
        defaultSideFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: null,
          props: {
            step: 0.5,
            precision: 1
          },
          dynamicProps: form => {
            return {
              visible: !!form.defaultSideFinishID
            };
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.defaultSideFinishID) }]
        },
        defaultBottomFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Bottom
            )
          }
        },
        defaultBottomFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: null,
          props: {
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: !!form.defaultBottomFinishID
            };
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.defaultBottomFinishID) }]
        }
      },
      {
        // Tab FinishTypes
        finishTreeData: {
          type: FinishTypesList,
          props: {
            caption: 'common.emptyString'
          }
        }
      },
      {
        // Tab ProductTypes
        productTypes: {
          type: FabricTypesList,
          props: {
            caption: 'common.emptyString'
          }
        }
      }
    ]
  };
}

const layoutTabGeneral = {
  named: true,
  sections: [
    {
      width: 50,
      fields: ['code', 'description', 'productGroupTypeOption', 'defaultParcelTypeID']
    },
    {
      width: 50,
      fields: [
        'defaultTopFinishID',
        'defaultTopFinishMeasurementInCm',
        'defaultSideFinishID',
        'defaultSideFinishMeasurementInCm',
        'defaultBottomFinishID',
        'defaultBottomFinishMeasurementInCm'
      ]
    }
  ]
};

const layoutFullWidth = {
  sections: [
    {
      width: 100
    }
  ]
};

export function getCreateLayoutDefinition(vue) {
  return {
    groups: [layoutTabGeneral]
  };
}

export function getEditLayoutDefinition(vue) {
  return {
    groups: [layoutTabGeneral, layoutFullWidth, layoutFullWidth]
  };
}

export function createItemFromFormData(vue, form) {
  const item = {
    ...form
  };
  delete item.finishTreeData;
  const finishTypes = [];
  if (form.finishTreeData) {
    form.finishTreeData.forEach(finishType => {
      const finishes = finishType.finishes.map(finish => {
        if (finish.checked || finish.applicableYN) {
          // if finish is used then make sure the parent finishType is used
          finishType.linkedYN = true;
        }
        return {
          finishID: finish.finishID,
          description: finish.description,
          applicableYN: finish.checked || finish.applicableYN,
          quantityFormula: finish.quantityFormula
        };
      });
      finishTypes.push({
        finishTypeID: finishType.finishTypeID,
        description: finishType.description,
        linkedYN: finishType.linkedYN,
        finishes: finishes
      });
    });
  }
  // send only used finish types
  item.finishTypes = finishTypes.filter(ft => ft.linkedYN);
  return item;
}

export function createFormDataFromItem(vue, item) {
  const form = {
    ...item
  };
  const finishTreeData = [];
  form.finishTypes.forEach(function(finishType) {
    if (finishType.finishes.length) {
      finishTreeData.push({
        ...finishType,
        treeIdentifier: 'ft_' + finishType.finishTypeID,
        finishes: finishType.finishes.map(finish => {
          return {
            ...finish,
            treeIdentifier: 'f_' + finish.finishID,
            isFinishType: false,
            isFinish: true,
            finishTypeID: finishType.finishTypeID,
            calculationMethodOption: finishType.calculationMethodOption,
            checked: finish.applicableYN
          };
        }),
        isFinishType: true,
        isFinish: false,
        checked: finishType.linkedYN
      });
    }
  });
  delete form.finishTypes;
  form.finishTreeData = finishTreeData;
  return form;
}
